import { EditIcon, EyeIcon, TrashIcon } from 'lucide-react';

import React from 'react';

import { ExpensePermissionService, formatDate } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  ExpenseResponseDto,
  PendingConversionFields,
} from '@splitfiesta/shared';
import { Button, Card } from '@splitfiesta/ui';

import { UserPositionIndicator } from '@/components/activity/UserPositionIndicator';
import { LockedAction } from '@/components/common/LockedAction';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';

import { resolveUserName } from '@/lib/displayName';

import { ExpenseCategoryIcon } from '@/utils/expenseCategoryIcons';

interface ExpensesTableProps {
  expenses: Array<ExpenseResponseDto & PendingConversionFields>;
  onDelete?: (expenseId: string) => void;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
  lastElementRef?:
    React.RefObject<HTMLDivElement> | ((node: HTMLElement | null) => void);
  showLoadingMore?: boolean;
  footer?: React.ReactNode;
  currentUserId?: string;
  groupMembers?: Array<{
    userId: string;
    role: 'owner' | 'admin' | 'member';
  }>;
}

export function ExpensesTable({
  expenses,
  onDelete,
  loading = false,
  emptyMessage,
  emptyDescription,
  className = '',
  lastElementRef,
  showLoadingMore = false,
  footer,
  currentUserId,
  groupMembers,
}: ExpensesTableProps) {
  const { t, i18n } = useTranslation();
  const lockedBody = t('expenses:permissions.lockedBody');

  if (loading && expenses.length === 0) {
    return (
      <Card className="overflow-hidden">
        <div className="-mx-6">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className={`py-4 flex items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 ${
                i !== 5 ? 'border-b border-gray-100 dark:border-gray-700' : ''
              }`}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse"></div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (expenses.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            {emptyMessage || t('groups:empty.noExpenses')}
          </p>
          {emptyDescription && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {emptyDescription}
            </p>
          )}
        </div>
      </Card>
    );
  }

  const getPayerName = (expense: ExpenseResponseDto): string => {
    if (expense.payers && expense.payers.length > 0) {
      if (expense.payers.length === 1) {
        return resolveUserName(expense.payers[0].userName, t);
      } else {
        return t('expenses:labels.multiplePayers', {
          count: expense.payers.length,
        });
      }
    }
    return t('common:labels.unknown');
  };

  return (
    <Card
      className={`overflow-hidden ${className}`}
      data-testid="expenses-table"
    >
      <div className="-mx-6">
        {expenses.map((expense, index) => {
          const expenseId = expense.id || `expense-${index}`;
          const isLastExpense = index === expenses.length - 1;
          const original =
            expense.originalAmount != null && expense.originalCurrency != null
              ? {
                  amount: expense.originalAmount,
                  currency: expense.originalCurrency,
                }
              : undefined;

          return (
            <div
              key={expenseId}
              ref={isLastExpense && lastElementRef ? lastElementRef : null}
              className={`group relative py-4 flex items-start sm:items-center justify-between gap-3 sm:gap-4 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors px-4 sm:px-6 ${
                index !== expenses.length - 1
                  ? 'border-b border-gray-100 dark:border-gray-700'
                  : ''
              }`}
              data-testid={`expense-row-${expenseId}`}
            >
              <RouteChangeLink
                href={`/groups/${expense.groupId}/expenses/${expenseId}`}
                className="absolute inset-0 z-0"
                aria-label={`View expense: ${expense.title}`}
              />

              {/* Left side: Icon and Expense Info */}
              <div className="flex items-start gap-3 flex-1 min-w-0 pointer-events-none">
                <ExpenseCategoryIcon
                  category={expense.category}
                  subcategory={expense.subcategory}
                  className="w-8 h-8 text-orange-500 dark:text-orange-400 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base line-clamp-2 sm:line-clamp-1 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                    {expense.title}
                  </h4>
                  {/* Mobile: stacked metadata */}
                  <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400 mt-0.5 space-y-0.5">
                    <div>{getPayerName(expense)}</div>
                    {expense.category && (
                      <div>{t(`expenses:categories.${expense.category}`)}</div>
                    )}
                    {expense.splits && expense.splits.length > 0 && (
                      <div>
                        {expense.splits.length} {t('common:labels.people')}
                      </div>
                    )}
                  </div>
                  {/* Desktop: inline layout */}
                  <div className="hidden sm:block space-y-0.5 mt-0.5">
                    <div className="flex items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
                      <span className="truncate">
                        {formatDate(expense.date, i18n.language)}
                      </span>
                      <span>•</span>
                      <span className="truncate">
                        {t('expenses:labels.paidBy', {
                          name: getPayerName(expense),
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {expense.category && (
                        <>
                          <span className="truncate">
                            {t(`expenses:categories.${expense.category}`)}
                          </span>
                          <span>•</span>
                        </>
                      )}
                      <span className="truncate">
                        {t(`expenses:splitMethods.${expense.splitMethod}`)}
                      </span>
                      {expense.splits && expense.splits.length > 0 && (
                        <>
                          <span>•</span>
                          <span>
                            {expense.splits.length} {t('common:labels.people')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side: Amount, Date (mobile), Position, Actions */}
              <div className="flex flex-col items-end justify-start gap-1 sm:gap-2 shrink-0 self-start sm:self-center">
                <div className="text-right pointer-events-none">
                  <div className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white">
                    <CurrencyDisplay
                      amount={expense.amount}
                      currency={expense.currency}
                      original={original}
                    />
                  </div>
                  {expense.pendingConversion === true && (
                    <div
                      data-testid="pending-conversion-hint"
                      className="text-xs text-gray-500 dark:text-gray-400"
                    >
                      {t('expenses:conversion.offlineQueued', {
                        date: formatDate(expense.date, i18n.language),
                      })}
                    </div>
                  )}
                  {/* Date below amount on mobile */}
                  <div className="sm:hidden text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(expense.date, i18n.language)}
                  </div>
                  {/* Position indicator */}
                  {currentUserId && (
                    <div className="mt-0.5 flex justify-end">
                      <UserPositionIndicator
                        userId={currentUserId}
                        paidBy={expense.payers?.map(p => ({
                          userId: p.userId,
                          amount: p.amount,
                        }))}
                        splits={expense.splits?.map(s => ({
                          userId: s.userId,
                          amount: s.amount,
                        }))}
                        currency={expense.currency}
                        variant="compact"
                      />
                    </div>
                  )}
                </div>
                {(() => {
                  const canEdit = ExpensePermissionService.canModifyExpense(
                    expense,
                    currentUserId,
                    groupMembers?.find(m => m.userId === currentUserId)?.role
                  );

                  return (
                    <div className="flex items-center gap-1 relative z-10">
                      <RouteChangeLink
                        href={`/groups/${expense.groupId}/expenses/${expense.id}`}
                        className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-colors"
                        title={t('common:actions.view')}
                        data-testid={`expense-view-button-${expenseId}`}
                      >
                        <EyeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </RouteChangeLink>
                      {canEdit ? (
                        <RouteChangeLink
                          href={`/groups/${expense.groupId}/expenses/${expenseId}/edit`}
                          className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors"
                          title={t('common:actions.edit')}
                          data-testid={`expense-edit-button-${expenseId}`}
                        >
                          <EditIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </RouteChangeLink>
                      ) : (
                        <LockedAction
                          message={lockedBody}
                          className="p-2"
                          testId={`expense-edit-button-${expenseId}`}
                        >
                          <EditIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        </LockedAction>
                      )}
                      {/* The locked Delete needs no handler, so it shows
                          even when the caller wires none. */}
                      {canEdit ? (
                        onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(expenseId)}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20"
                            title={t('common:actions.delete')}
                            data-testid={`expense-delete-button-${expenseId}`}
                          >
                            <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                          </Button>
                        )
                      ) : (
                        <LockedAction
                          message={lockedBody}
                          className="p-2"
                          testId={`expense-delete-button-${expenseId}`}
                        >
                          <TrashIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        </LockedAction>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Loading indicator for infinite scroll */}
      {showLoadingMore && (
        <div className="px-6 py-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 dark:border-orange-400"></div>
          </div>
        </div>
      )}

      {/* Footer content */}
      {footer}
    </Card>
  );
}
